import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const WingForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const [sites, setSites] = useState([]);
    const [formData, setFormData] = useState({
        wingId: null,
        siteId: null,
        wingName: '',
        isActive: 'Active'
    });

    const isEdit = !!location.state?.id;
    const editItem = location.state?.item;

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                // Fetch Residential Sites for dropdown
                const siteRes = await apiServices({
                    method: 'GET',
                    url: '/ResidentialSite/GetAllResidentialSites'
                });

                if (siteRes.success || siteRes.isSuccess || siteRes.IsSuccess || siteRes) {
                    let siteData = [];
                    let actualData = siteRes.data || siteRes;
                    if (Array.isArray(actualData)) {
                        siteData = actualData;
                    } else if (actualData.data && Array.isArray(actualData.data)) {
                        siteData = actualData.data;
                    }

                    const formattedSites = siteData
                        .filter(s => s.IsActive === 'Active' || s.isActive === 'Active')
                        .map(s => ({
                            label: s.SiteName || s.siteName,
                            value: s.SiteId || s.siteId
                        }));
                    setSites(formattedSites);
                }

                // If edit mode, populate data from the passed item
                if (isEdit && editItem) {
                    setFormData({
                        wingId: editItem.wingId || editItem.WingId || null,
                        siteId: editItem.siteId || editItem.SiteId || null,
                        wingName: editItem.wingName || editItem.WingName || '',
                        isActive: editItem.isActive || editItem.IsActive || (editItem.isActiveId === 1 ? 'Active' : 'Inactive') || 'Active'
                    });
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to load necessary data.");
            }
        };

        fetchMasterData();
    }, [isEdit, editItem]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => ({ ...prev, [key]: option ? option.value : null }));
    };

    const validateForm = () => {
        if (!formData.siteId) {
            toast.warning("Site is required");
            return false;
        }
        if (!formData.wingName || !formData.wingName.trim()) {
            toast.warning("Wing Name is required");
            return false;
        }
        if (!formData.isActive) {
            toast.warning("Status is required");
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payload = {
            wingId: formData.wingId,
            siteId: formData.siteId,
            wingName: formData.wingName,
            createdBy: 'sys',
            isActiveId: formData.isActive === 'Active' ? 1 : 0
        };

        try {
            const url = isEdit ? `/ResWingMaster/putWing` : `/ResWingMaster/postWing`;
            const method = isEdit ? 'PUT' : 'POST';

            const response = await apiServices({ method, url, data: payload });

            if (response.success || response.isSuccess || response.IsSuccess || response) {
                toast.success(`Residential Wing ${isEdit ? 'updated' : 'created'} successfully!`);
                navigate('/zentres/master-management/wing');
            } else {
                toast.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} wing.`);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    const selectedSite = sites.find(s => s.value === formData.siteId) || null;

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Residential Wing`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} residential wing.`}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <CustomSelect
                    label={<>Site Name <span className="text-red-500">*</span></>}
                    options={sites}
                    value={selectedSite}
                    onChange={(opt) => handleSelectChange('siteId', opt)}
                />

                <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="wingName">
                        Wing Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:border-indigo-500 transition-colors"
                        type="text"
                        id="wingName"
                        value={formData.wingName}
                        onChange={handleInputChange}
                        placeholder="e.g., North Wing"
                    />
                </div>

                <CustomSelect
                    label={<>Status <span className="text-red-500">*</span></>}
                    options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                    value={formData.isActive ? { label: formData.isActive, value: formData.isActive } : null}
                    onChange={(opt) => handleSelectChange('isActive', opt)}
                />
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentres/master-management/wing")}
                    className="px-3 py-2 border rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default WingForm;
